import { useCurrentWorkspace } from '@gripp/shared-logic';
import { SlideAction, useSlideLayout } from '@gripp/shared-ui';
import { Icon } from 'libs/shared-ui/src/components/crop-circle/icons/Icon';
import { Pressable, StyleSheet, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router';
import { AddButton } from '../../shared/components/addButton/addButton';
import { useAssetRouting } from '../assetRouteContext';
import {
  isActivityOrEditPage,
  shouldUseAssetSubrouteInitialTransition,
} from '../assetUtils';

export const AssetFilters = ({
  assetId,
  issueId,
}: {
  assetId: string;
  issueId?: string;
}) => {
  if (!assetId) {
    return null;
  }

  return <AssetFiltersBase assetId={assetId} issueId={issueId} />;
};

export const AssetFiltersBase = ({
  assetId,
  issueId: issueIdProp,
}: {
  assetId: string;
  issueId?: string;
}) => {
  const { onSlideChange, prepareSlideTransition } = useSlideLayout();
  const location = useLocation();
  const navigate = useNavigate();
  const { basePath } = useAssetRouting(assetId);
  const workspace = useCurrentWorkspace();

  // issueId may come from props (ActivityConversationPanel passes it explicitly)
  // or from URL params (activity-context asset pages like /activities/:id/asset/:id/edit).
  const { issueId: routeIssueId } = useParams();
  const issueId = issueIdProp ?? routeIssueId;

  const onAssetEdit = async () => {
    // In activities context, use the activity-prefixed URL so back navigation
    // returns to the asset panel inside activities (/activities/:id/asset/:id).
    const editPath = issueId
      ? `/activities/${issueId}/asset/${assetId}/edit`
      : `${basePath}/edit`;

    if (shouldUseAssetSubrouteInitialTransition(location)) {
      prepareSlideTransition(SlideAction.NEXT);
      navigate(editPath, {
        state: { slideLayoutInitialTransition: SlideAction.NEXT },
      });
      return;
    }

    if (!isActivityOrEditPage(location)) {
      await onSlideChange(SlideAction.NEXT);
    }

    navigate(editPath);
  };

  const onAddNewIssue = () => {
    const newIssuePath = issueId
      ? `/activities/${issueId}/asset/${assetId}/activity/new`
      : `${basePath}/activity/new`;
    if (shouldUseAssetSubrouteInitialTransition(location)) {
      prepareSlideTransition(SlideAction.NEXT);
      navigate(newIssuePath, {
        state: { slideLayoutInitialTransition: SlideAction.NEXT },
      });
      return;
    }

    navigate(newIssuePath);
  };

  return (
    <View style={styles.filtersContainer}>
      <Pressable onPress={onAssetEdit} testID="asset-card-edit-button">
        <Icon name="Settings" size={24} />
      </Pressable>
      {workspace && <AddButton onPress={onAddNewIssue} />}
    </View>
  );
};

const styles = StyleSheet.create({
  filtersContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 16,
  },
  modal: {
    alignSelf: 'center',
    backgroundColor: 'white',
    padding: 0,
    margin: 0,
    borderRadius: 10,
    width: 560,
    minHeight: 400,
  },
});
